import { useEffect, useState } from 'react';
import { CpmButton, CpmModal } from '../../../../components/design-system';
import {
    CPM_VOLUME_DEFAULT_UNIT,
    CPM_VOLUME_SETTING_STATUS,
    CPM_VOLUME_UNIT_OPTIONS,
} from '../../../../constants/centralProductMaster.constants';
import { getVolumeSettingUsage } from '../../../../services/productMasterService';
import type { CpmVolumeSetting } from '../../../../types/centralProductMaster.types';

export interface VolumeFormValues {
    value: number;
    unit: string;
    status: string;
}

type FormPhase = 'idle' | 'loading' | 'fetchError' | 'saving' | 'done';
type ImpactPhase = 'idle' | 'loading' | 'ready' | 'error';

interface VolumeFormModalProps {
    open: boolean;
    editing: CpmVolumeSetting | null;
    /** When editing, parent sets true while the open-fetch skeleton is showing. */
    formPhase: FormPhase;
    onClose: () => void;
    onSave: (values: VolumeFormValues) => Promise<string | null>;
    onRetryFetch?: () => void;
}

export default function VolumeFormModal({
    open,
    editing,
    formPhase,
    onClose,
    onSave,
    onRetryFetch,
}: VolumeFormModalProps) {
    const isEdit = !!editing;
    const [value, setValue] = useState('');
    const [unit, setUnit] = useState(CPM_VOLUME_DEFAULT_UNIT);
    const [status, setStatus] = useState<
        (typeof CPM_VOLUME_SETTING_STATUS)[keyof typeof CPM_VOLUME_SETTING_STATUS]
    >(CPM_VOLUME_SETTING_STATUS.ACTIVE);
    const [error, setError] = useState('');
    const [localPhase, setLocalPhase] = useState<FormPhase>('idle');
    const [impactPhase, setImpactPhase] = useState<ImpactPhase>('idle');
    const [impactCount, setImpactCount] = useState(0);

    const phase = formPhase === 'loading' || formPhase === 'fetchError' ? formPhase : localPhase;
    const locked = phase === 'saving' || phase === 'done';

    const loadImpact = async (id: string) => {
        setImpactPhase('loading');
        try {
            const data = await getVolumeSettingUsage({ id });
            setImpactCount(data.usageCount ?? 0);
            setImpactPhase('ready');
        } catch {
            setImpactPhase('error');
        }
    };

    useEffect(() => {
        if (!open) return;
        if (formPhase === 'loading' || formPhase === 'fetchError') {
            setLocalPhase(formPhase);
            setError('');
            return;
        }
        if (editing) {
            setValue(String(editing.value));
            setUnit(editing.unit || CPM_VOLUME_DEFAULT_UNIT);
            setStatus(
                editing.status === CPM_VOLUME_SETTING_STATUS.INACTIVE
                    ? CPM_VOLUME_SETTING_STATUS.INACTIVE
                    : CPM_VOLUME_SETTING_STATUS.ACTIVE
            );
            setLocalPhase('idle');
            void loadImpact(editing._id);
        } else {
            setValue('');
            setUnit(CPM_VOLUME_DEFAULT_UNIT);
            setStatus(CPM_VOLUME_SETTING_STATUS.ACTIVE);
            setLocalPhase('idle');
            setImpactPhase('idle');
            setImpactCount(0);
        }
        setError('');
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when open/editing/formPhase change
    }, [open, editing, formPhase]);

    const canSave = value.trim().length > 0 && !locked;

    const handleSave = async () => {
        if (locked) return;
        const raw = String(value || '').replace(/[^0-9.]/g, '');
        const num = parseFloat(raw);
        if (!raw || !Number.isFinite(num) || num <= 0) {
            setError('Enter a volume value greater than zero, for example 750.');
            return;
        }
        setLocalPhase('saving');
        setError('');
        const err = await onSave({ value: num, unit, status });
        if (err) {
            setLocalPhase('idle');
            setError(err);
            return;
        }
        setLocalPhase('done');
        window.setTimeout(() => {
            onClose();
            setLocalPhase('idle');
        }, 900);
    };

    const formFootnote =
        phase === 'saving'
            ? 'Saving…'
            : phase === 'done'
              ? 'Saved'
              : isEdit
                ? 'Sorted by value on save'
                : 'Created as ACTIVE';

    const saveLabel =
        phase === 'saving'
            ? isEdit
                ? 'Saving'
                : 'Adding'
            : phase === 'done'
              ? 'Saved'
              : isEdit
                ? 'Save volume'
                : 'Add volume';

    return (
        <CpmModal open={open} onClose={locked ? () => undefined : onClose} maxWidth={520} zIndex={70}>
            {phase === 'loading' ? (
                <div className="flex flex-col" style={{ gap: 16, padding: 24 }}>
                    <div className="flex items-center" style={{ gap: 10 }}>
                        <span className="cpm-spin" />
                        <span
                            className="uppercase text-[var(--cpm-text-tertiary)]"
                            style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em' }}
                        >
                            Loading volume
                        </span>
                    </div>
                    <span className="cpm-sk" style={{ height: 22, width: '56%' }} />
                    <span className="cpm-sk" style={{ height: 13, width: '82%' }} />
                    <div className="flex" style={{ gap: 12 }}>
                        <span className="cpm-sk" style={{ flex: 1, height: 42, borderRadius: 10 }} />
                        <span className="cpm-sk" style={{ width: 150, height: 42, borderRadius: 10 }} />
                    </div>
                    <span className="cpm-sk" style={{ height: 34, width: 200, borderRadius: 10 }} />
                </div>
            ) : null}

            {phase === 'fetchError' ? (
                <div
                    className="flex flex-col items-center text-center"
                    style={{ gap: 13, padding: '34px 26px 28px' }}
                >
                    <span
                        className="inline-flex items-center justify-center text-[var(--cpm-shortfall-text)]"
                        style={{
                            width: 38,
                            height: 38,
                            borderRadius: 10,
                            background: 'var(--cpm-shortfall-bg)',
                        }}
                    >
                        <svg
                            width="19"
                            height="19"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                            <path d="M12 9v4" />
                            <path d="M12 17h.01" />
                        </svg>
                    </span>
                    <h2 className="m-0 !text-[18px] !font-extrabold !tracking-[-0.02em] !text-[var(--cpm-brand-navy)]">
                        Couldn't open this volume
                    </h2>
                    <p className="m-0 max-w-[380px] !text-[13px] !font-semibold !leading-[1.5] !text-[var(--cpm-text-secondary)]">
                        The record didn't load, so there is nothing to edit yet. Nothing has
                        changed.
                    </p>
                    <div className="flex items-center" style={{ gap: 10, marginTop: 4 }}>
                        <CpmButton variant="outline" size="modal" onClick={onClose}>
                            Close
                        </CpmButton>
                        <CpmButton
                            variant="navy"
                            size="modal"
                            leadingIcon={
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                                    <path d="M3 3v5h5" />
                                </svg>
                            }
                            onClick={() => onRetryFetch?.()}
                        >
                            Retry
                        </CpmButton>
                    </div>
                </div>
            ) : null}

            {phase !== 'loading' && phase !== 'fetchError' ? (
                <>
                    <div
                        className="flex items-start border-b border-[var(--cpm-border-default)]"
                        style={{ gap: 14, padding: '22px 24px 18px' }}
                    >
                        <span
                            className="shrink-0 inline-flex items-center justify-center text-[var(--cpm-brand-purple)]"
                            style={{
                                width: 38,
                                height: 38,
                                borderRadius: 10,
                                background: 'var(--cpm-purple-tint-13)',
                            }}
                        >
                            {isEdit ? (
                                <svg
                                    width="19"
                                    height="19"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M12 20h9" />
                                    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
                                </svg>
                            ) : (
                                <svg
                                    width="19"
                                    height="19"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M12 5v14" />
                                    <path d="M5 12h14" />
                                </svg>
                            )}
                        </span>
                        <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 6 }}>
                            <span
                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em' }}
                            >
                                Volume settings
                            </span>
                            <h2 className="m-0 !text-[21px] !font-extrabold !tracking-[-0.02em] !leading-[1.15] !text-[var(--cpm-brand-navy)]">
                                {isEdit ? 'Edit volume' : 'Add volume'}
                            </h2>
                            <p className="m-0 !text-[13px] !font-semibold !leading-[1.5] !text-[var(--cpm-text-secondary)]">
                                {isEdit
                                    ? 'Changes apply to what the Variant create dropdown offers. No existing variant is rewritten.'
                                    : 'Enter the pack size as it should read in the create dropdown, for example 750 ML.'}
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col" style={{ padding: '20px 24px', gap: 16 }}>
                        <div className="flex items-start" style={{ gap: 12 }}>
                            <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 6 }}>
                                <span
                                    className="uppercase text-[var(--cpm-text-secondary)]"
                                    style={{
                                        fontSize: 10.5,
                                        fontWeight: 800,
                                        letterSpacing: '0.06em',
                                    }}
                                >
                                    Value
                                </span>
                                <input
                                    value={value}
                                    disabled={locked}
                                    onChange={(e) => {
                                        setValue(e.target.value);
                                        setError('');
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') void handleSave();
                                    }}
                                    inputMode="numeric"
                                    placeholder="e.g. 750"
                                    autoFocus
                                    className="rounded-[10px] border-[1.5px] border-[var(--cpm-border-strong)] text-[var(--cpm-brand-navy)] tabular-nums focus:outline-none focus:border-[var(--cpm-brand-purple)] focus:shadow-[0_0_0_3px_rgba(110,100,240,0.28)] disabled:bg-[var(--cpm-surface-subtle)]"
                                    style={{
                                        height: 42,
                                        padding: '0 13px',
                                        fontSize: 15,
                                        fontWeight: 800,
                                        background: locked ? 'var(--cpm-surface-subtle)' : '#fff',
                                    }}
                                />
                            </div>
                            <div className="flex flex-col" style={{ width: 150, gap: 6 }}>
                                <span
                                    className="uppercase text-[var(--cpm-text-secondary)]"
                                    style={{
                                        fontSize: 10.5,
                                        fontWeight: 800,
                                        letterSpacing: '0.06em',
                                    }}
                                >
                                    Unit
                                </span>
                                <select
                                    value={unit}
                                    disabled={locked}
                                    onChange={(e) => {
                                        setUnit(e.target.value);
                                        setError('');
                                    }}
                                    className="w-full box-border rounded-[10px] border-[1.5px] border-[var(--cpm-border-strong)] text-[var(--cpm-brand-navy)] appearance-none cursor-pointer outline-none transition-[border-color,box-shadow] duration-150 ease-out hover:border-[var(--cpm-brand-purple)] focus:border-[var(--cpm-brand-purple)] focus:shadow-[0_0_0_3px_rgba(110,100,240,0.28)] disabled:cursor-not-allowed disabled:bg-[var(--cpm-surface-subtle)]"
                                    style={{
                                        height: 42,
                                        padding: '0 40px 0 14px',
                                        fontSize: 14.5,
                                        fontWeight: 800,
                                        backgroundColor: locked
                                            ? 'var(--cpm-surface-subtle)'
                                            : '#fff',
                                        backgroundImage:
                                            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23717182' stroke-width='2.25' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'right 13px center',
                                    }}
                                >
                                    {CPM_VOLUME_UNIT_OPTIONS.map((u) => (
                                        <option key={u.value} value={u.value}>
                                            {u.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {isEdit ? (
                            <div className="flex flex-col" style={{ gap: 6 }}>
                                <div className="flex items-center" style={{ gap: 8 }}>
                                    <span
                                        className="uppercase text-[var(--cpm-text-secondary)]"
                                        style={{
                                            fontSize: 10.5,
                                            fontWeight: 800,
                                            letterSpacing: '0.06em',
                                        }}
                                    >
                                        Status
                                    </span>
                                    {impactPhase === 'loading' ? (
                                        <span
                                            className="inline-flex items-center text-[var(--cpm-text-tertiary)]"
                                            style={{ gap: 6, fontSize: 11, fontWeight: 700 }}
                                        >
                                            <span
                                                className="cpm-spin"
                                                style={{ width: 11, height: 11, borderWidth: 1.8 }}
                                            />
                                            Checking how many variants use this size
                                        </span>
                                    ) : null}
                                </div>
                                <div className="flex items-center" style={{ gap: 8 }}>
                                    <button
                                        type="button"
                                        disabled={locked}
                                        onClick={() => setStatus(CPM_VOLUME_SETTING_STATUS.ACTIVE)}
                                        className="inline-flex items-center rounded-[10px] border-[1.5px] cursor-pointer transition-colors duration-150 disabled:cursor-not-allowed"
                                        style={{
                                            height: 34,
                                            padding: '0 14px',
                                            fontSize: 12.5,
                                            fontWeight: 800,
                                            opacity: locked ? 0.6 : 1,
                                            borderColor:
                                                status === CPM_VOLUME_SETTING_STATUS.ACTIVE
                                                    ? 'var(--cpm-success-border)'
                                                    : 'var(--cpm-border-strong)',
                                            background:
                                                status === CPM_VOLUME_SETTING_STATUS.ACTIVE
                                                    ? 'var(--cpm-success-bg)'
                                                    : '#fff',
                                            color:
                                                status === CPM_VOLUME_SETTING_STATUS.ACTIVE
                                                    ? 'var(--cpm-success-text-light)'
                                                    : 'var(--cpm-text-secondary)',
                                        }}
                                    >
                                        Active
                                    </button>
                                    <button
                                        type="button"
                                        disabled={locked}
                                        onClick={() =>
                                            setStatus(CPM_VOLUME_SETTING_STATUS.INACTIVE)
                                        }
                                        className="inline-flex items-center rounded-[10px] border-[1.5px] cursor-pointer transition-colors duration-150 disabled:cursor-not-allowed"
                                        style={{
                                            height: 34,
                                            padding: '0 14px',
                                            fontSize: 12.5,
                                            fontWeight: 800,
                                            opacity: locked ? 0.6 : 1,
                                            borderColor:
                                                status === CPM_VOLUME_SETTING_STATUS.INACTIVE
                                                    ? 'var(--cpm-warning-border)'
                                                    : 'var(--cpm-border-strong)',
                                            background:
                                                status === CPM_VOLUME_SETTING_STATUS.INACTIVE
                                                    ? 'var(--cpm-warning-bg)'
                                                    : '#fff',
                                            color:
                                                status === CPM_VOLUME_SETTING_STATUS.INACTIVE
                                                    ? 'var(--cpm-warning-text)'
                                                    : 'var(--cpm-text-secondary)',
                                        }}
                                    >
                                        Inactive
                                    </button>
                                </div>
                                {impactPhase === 'ready' ? (
                                    <span
                                        style={{
                                            fontSize: 11.5,
                                            fontWeight: 600,
                                            lineHeight: 1.45,
                                            color: 'var(--cpm-text-tertiary)',
                                        }}
                                    >
                                        {impactCount.toLocaleString('en-IN')} variants are already
                                        saved at this size. Inactive removes it from the create
                                        dropdown going forward; none of them are rewritten.
                                    </span>
                                ) : null}
                                {impactPhase === 'error' ? (
                                    <span
                                        className="inline-flex items-center text-[var(--cpm-warning-text)]"
                                        style={{ gap: 7, fontSize: 11.5, fontWeight: 700 }}
                                    >
                                        <svg
                                            width="13"
                                            height="13"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="var(--cpm-warning-icon, var(--cpm-warning-text))"
                                            strokeWidth="2.2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="shrink-0"
                                        >
                                            <circle cx="12" cy="12" r="10" />
                                            <path d="M12 8v4" />
                                            <path d="M12 16h.01" />
                                        </svg>
                                        Couldn't check how many variants use this size.
                                        <button
                                            type="button"
                                            className="underline cursor-pointer bg-transparent border-0 p-0 text-inherit font-inherit"
                                            onClick={() => {
                                                if (editing) void loadImpact(editing._id);
                                            }}
                                        >
                                            Retry
                                        </button>
                                    </span>
                                ) : null}
                            </div>
                        ) : null}

                        {error ? (
                            <div
                                className="flex items-start rounded-[10px] border border-[var(--cpm-shortfall-border)] bg-[var(--cpm-shortfall-bg)]"
                                style={{ gap: 9, padding: '11px 13px' }}
                            >
                                <svg
                                    width="15"
                                    height="15"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="var(--cpm-shortfall-text)"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="shrink-0"
                                    style={{ marginTop: 1 }}
                                >
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M12 8v4" />
                                    <path d="M12 16h.01" />
                                </svg>
                                <span
                                    className="text-[var(--cpm-shortfall-text)]"
                                    style={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.45 }}
                                >
                                    {error}
                                </span>
                            </div>
                        ) : null}

                        {phase === 'done' ? (
                            <div
                                className="flex items-center rounded-[10px] border border-[var(--cpm-success-border)] bg-[var(--cpm-success-bg)]"
                                style={{ gap: 9, padding: '11px 13px' }}
                            >
                                <svg
                                    width="15"
                                    height="15"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="var(--cpm-success-dot)"
                                    strokeWidth="2.6"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="shrink-0"
                                >
                                    <path d="M20 6 9 17l-5-5" />
                                </svg>
                                <span
                                    className="text-[var(--cpm-success-text-light)]"
                                    style={{ fontSize: 12.5, fontWeight: 800, lineHeight: 1.45 }}
                                >
                                    {isEdit
                                        ? 'Saved. The create dropdown is up to date.'
                                        : 'Added. It is now selectable when creating a variant.'}
                                </span>
                            </div>
                        ) : null}
                    </div>

                    <div
                        className="flex items-center border-t-[1.5px] border-[var(--cpm-border-strong)]"
                        style={{
                            gap: 10,
                            padding: '16px 24px',
                            background: 'rgba(249,249,252,0.88)',
                        }}
                    >
                        <span
                            style={{
                                fontSize: 11.5,
                                fontWeight: 700,
                                color: 'var(--cpm-text-secondary)',
                            }}
                        >
                            {formFootnote}
                        </span>
                        <div className="ml-auto flex items-center" style={{ gap: 10 }}>
                            <CpmButton
                                variant="outline"
                                size="modal"
                                onClick={onClose}
                                disabled={locked}
                            >
                                Cancel
                            </CpmButton>
                            <CpmButton
                                variant={phase === 'done' ? 'success' : 'navy'}
                                size="modal"
                                onClick={() => void handleSave()}
                                disabled={!canSave && phase !== 'done'}
                                isLoading={phase === 'saving'}
                                leadingIcon={
                                    phase === 'done' ? (
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.8"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M20 6 9 17l-5-5" />
                                        </svg>
                                    ) : undefined
                                }
                            >
                                {saveLabel}
                            </CpmButton>
                        </div>
                    </div>
                </>
            ) : null}
        </CpmModal>
    );
}
